"use client";

/**
 * HeroBrochureAanvraag — CRO hoofdblok voor badkamerrenovatie.
 *
 * Port van Claude Design "CRO Hoofdblok.html". Bestaat uit:
 *  - links: eyebrow + serif-headline met cursieve plaats + body + ronde CTA
 *    + trust-regel + Google-review kaart
 *  - rechts: optionele actie-banner (gradient, met countdown) + 3-staps
 *    brochure-aanvraag formulier (stap 1: aanhef/achternaam/telefoon/email →
 *    stap 2: postcode+huisnummer met mock-lookup → stap 3: oppervlakte+wanneer
 *    chips → success-card).
 *
 * Progressieve save via /api/brochure-lead (zelfde endpoint als BrochureForm):
 *   POST step 1 → lead_id  |  PATCH step 2  |  PATCH step 3 (inclusief
 *   aanhef+oppervlakte+wanneer gebundeld in special_requirements).
 *
 * Kleuren komen via CSS vars uit themeConfigToCss, zodat elk thema-palet
 * automatisch doorwerkt. De "actie" (promo-banner tekst + deadline) is
 * volledig per blok configureerbaar — zo kan elke landingspagina een andere
 * aanbieding tonen.
 */

import { useEffect, useRef, useState } from "react";
import { preload } from "react-dom";
import NextImage from "next/image";
import type { DomainConfig } from "../types";
import { getAttribution } from "../lib/attribution";
import { pushDataLayer, sha256Hex } from "../lib/gtm";
import { isValidEmail } from "../lib/email";
import TurnstileWidget from "./TurnstileWidget";
import { buildImageAlt } from "../lib/image-alt";

// ── Configuratie-contract dat via PageBlock.content binnenkomt ──────────────
export interface HeroBrochureAanvraagContent {
  city?: string;
  /** Optionele achtergrondfoto (badkamer-sfeer foto). Wordt rechts naast de
   *  form gerenderd op desktop, full-width above-the-fold op mobiel. Geeft
   *  een sterke LCP-candidate voor Lighthouse. */
  image_src?: string;
  image_alt?: string;
  /** Mobile-only hero foto die volledige breedte over het bovenste 320px van
   *  het scherm rendert, met de eyebrow-badge + headline in wit eroverheen.
   *  Verhoogt herkenning van de hoofdcluster (instapbad / inloopdouche / etc.)
   *  zodra senior op de pagina landt. Wordt op desktop genegeerd. */
  mobile_hero_image_src?: string;
  mobile_hero_image_alt?: string;
  /** Optionele eigen Google-review afbeelding die rechtsonder in de mobile
   *  hero-foto verschijnt. Als leeg: auto-rendered pill (G-logo + 5 sterren
   *  + score). Als gevuld: jouw eigen image (bv. screenshot Google-listing). */
  mobile_google_review_image_src?: string;
  mobile_google_review_image_alt?: string;
  eyebrow?: string;
  headline_lead?: string;
  headline_italic?: string;
  headline_tail?: string;
  body?: string;
  cta_text?: string;
  trust_items?: Array<{ icon?: string; label: string }>;
  /** Override voor de 3 USP-chips onder de body op mobile. Default = "Binnen 1 dag",
   *  "Volledig op maat", "Eigen vakmensen". Per landingspagina overrijdbaar zodat
   *  bv. badkamer-renovatie cluster "2-5 dagen" toont i.p.v. "1 dag". Icon-namen
   *  matchen de Icon-component (clock, tool, medal, shield, calendar, etc.). */
  mobile_usps?: Array<{ icon?: string; label_top?: string; label_bottom?: string }>;
  google_score?: number;
  google_score_label?: string;
  promo?: {
    enabled?: boolean;
    eyebrow?: string;
    title?: string;
    subtitle?: string;
    deadline_iso?: string;
    /** Als true: title rolleert per kwartaal (Voorjaars-/Zomer-/Najaars-/Winterkorting)
     *  en deadline is een rollende 12-daagse cyclus i.p.v. vaste datum. Admin-title
     *  en deadline_iso worden dan genegeerd. */
    auto_seasonal?: boolean;
    /** Slug van een actie uit promo_actions — wordt meegestuurd als hidden field
     *  bij form-submit. In de mail wordt 'm als actie-blok gerenderd. */
    action_slug?: string;
    /** Server-side gehydrateerd uit promo_actions via enrichBlocksWithPromoActions.
     *  Als aanwezig: overschrijft title + icon (countdown-teller blijft werken). */
    action_data?: {
      slug: string;
      label: string;
      value_label: string | null;
      icon: string | null;
    };
  };
  form?: {
    oppervlakte_options?: string[];
    wanneer_options?: string[];
    oplossing_options?: string[];
    step1_title?: string;
    step2_title?: string;
    step3_title?: string;
  };
  /** Plaats van het brochure-formulier op desktop. Default "right" (form
   *  rechts, content links). "left" draait de kolom-volgorde om voor
   *  Ads-landings die direct met form willen openen. Mobiel altijd
   *  gestapeld (form onder content) — flag is desktop-only. */
  form_position?: "left" | "right";
  /** Voor-na video onder Hans in form-left mode (alleen actief wanneer
   *  form_position="left"). Bestandstype mp4/webm; autoplay+muted+loop. */
  voor_na_video_src?: string;
  voor_na_video_poster?: string;
  voor_na_video_alt?: string;
  voor_na_video_caption?: string;
  /** Container aspect-ratio voor video. Default "4 / 5" (portrait,
   *  hoger dan breed). Alternatieven: "16 / 9" landscape, "1 / 1"
   *  square, "3 / 4" sterk portrait. Source-frames worden gecropt
   *  via objectFit cover om de container te vullen. */
  voor_na_video_aspect_ratio?: string;
}

interface Props {
  content: HeroBrochureAanvraagContent;
  domain: DomainConfig;
  niche?: string;
}

// ── Token-mapping naar het thema (valt terug op ontwerp-defaults) ───────────
const T = {
  bg: "var(--cream, #FBF7F2)",
  accent: "var(--sage, #2E5D4F)",
  accentDark: "var(--sage, #1F4237)",
  accentSoft: "var(--sage-light, #D7E4DC)",
  ink: "var(--ink, #1A201D)",
  muted: "var(--stone, #667068)",
  border: "rgba(0,0,0,0.09)",
  card: "#FFFFFF",
  cta: "var(--terracotta, #FF6B1A)",
  ctaDark: "var(--terracotta-hover, #E05510)",
  ctaSoft: "var(--terracotta-light, #FFE4D2)",
};

const DUTCH_MONTHS = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

const DEFAULT_OPPERVLAKTE = ["< 4 m²", "4–6 m²", "6–9 m²", "> 9 m²", "Weet ik niet"];
const DEFAULT_WANNEER = ["Zo snel mogelijk", "Binnen 3 maanden", "Binnen een half jaar", "Ik oriënteer me"];
const DEFAULT_OPLOSSING = ["Van bad naar douche", "Van douche naar douche", "Totaalrenovatie", "Instapbad"];

// Echte lookup via onze /api/postcode route (PDOK Locatieserver).
// Valt stil terug op `null` zodat de form `addrError` toont i.p.v. te crashen.
async function lookupPostcode(
  pc: string,
  huisnummer: string,
  signal?: AbortSignal
): Promise<{ straat: string; woonplaats: string } | null> {
  const postcode = pc.replace(/\s/g, "").toUpperCase();
  // NL = 4 cijfers + 2 letters (1234AB), BE = 4 cijfers (2000-9999)
  if (!/^(\d{4}[A-Z]{2}|\d{4})$/.test(postcode) || !huisnummer.trim()) return null;
  try {
    const url = `/api/postcode?pc=${encodeURIComponent(postcode)}&hn=${encodeURIComponent(huisnummer.trim())}`;
    const res = await fetch(url, signal ? { signal } : undefined);
    if (!res.ok) return null;
    const body = (await res.json()) as { found?: boolean; straat?: string; woonplaats?: string };
    if (!body.found || !body.straat || !body.woonplaats) return null;
    return { straat: body.straat, woonplaats: body.woonplaats };
  } catch {
    return null;
  }
}

function interpolate(template: string | undefined, city: string): string {
  if (!template) return "";
  return template.replace(/\{\{\s*city\s*\}\}/gi, city);
}
function hasCityPlaceholder(s: string): boolean {
  return /\{\{\s*city\s*\}\}/i.test(s);
}

// ── Icons ──────────────────────────────────────────────────────────────────
function Icon({ name, size = 18, color = "currentColor" }: { name: string; size?: number; color?: string }) {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "user": return <svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21c1-4 4.5-6 8-6s7 2 8 6" /></svg>;
    case "phone": return <svg {...p}><path d="M22 16.9v3a2 2 0 01-2.2 2 19 19 0 01-8.3-3 19 19 0 01-6-6A19 19 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.6a2 2 0 01-.5 2.1L8 9.6a16 16 0 006 6l1.2-1.2a2 2 0 012.1-.5c.8.3 1.7.5 2.6.6a2 2 0 011.7 2z" /></svg>;
    case "mail": return <svg {...p}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M3 6l9 7 9-7" /></svg>;
    case "pin": return <svg {...p}><path d="M12 22s8-7 8-13a8 8 0 10-16 0c0 6 8 13 8 13z" /><circle cx="12" cy="9" r="3" /></svg>;
    case "check": return <svg {...p}><path d="M5 12l5 5L20 7" /></svg>;
    case "arrow": return <svg {...p}><path d="M5 12h14M13 5l7 7-7 7" /></svg>;
    case "back": return <svg {...p}><path d="M19 12H5M11 19l-7-7 7-7" /></svg>;
    case "lock": return <svg {...p}><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /></svg>;
    case "clock": return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
    case "shield": return <svg {...p}><path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" /><path d="M9 12l2 2 4-4" /></svg>;
    case "medal": return <svg {...p}><circle cx="12" cy="9" r="5" /><path d="M8.5 13l-2 8 5.5-3 5.5 3-2-8" /></svg>;
    case "building": return <svg {...p}><path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-7h6v7" /><path d="M3 21h18" /></svg>;
    case "tool": return <svg {...p}><path d="M14.7 6.3a4 4 0 01-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 015.4-5.4l-2.7 2.7-2-2 2.7-2.7z" /></svg>;
    default: return null;
  }
}

// ── Countdown hook ─────────────────────────────────────────────────────────
function useCountdown(deadline: number) {
  // SSR-safe init: gebruik deadline zelf (diff=0) zodat server en client
  // identieke HTML produceren. useEffect zet daarna direct na hydratie de
  // echte Date.now() — bezoeker ziet hooguit 16ms '00:00' voor de tikken.
  const [now, setNow] = useState<number>(deadline);
  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, deadline - now);
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
    expired: diff === 0,
  };
}

// ── Hoofdcomponent ─────────────────────────────────────────────────────────
export default function HeroBrochureAanvraag({ content, domain, niche = "" }: Props) {
  // Zwaluw-host detect — schakelt R01 extras aan (Hans-strip, USP-pillars,
  // sfeer-marmer-bg). Vd erft Zwaluw-presentatie 1-op-1 (zelfde merk-thema).
  const _hd = (domain.domain ?? "").replace(/^www\./, "");
  const isZwaluw =
    _hd.endsWith("zwaluwcomfortsanitair.nl") ||
    _hd.endsWith("veiligdouchen.nl") ||
    _hd.endsWith("veiligdouchen.be");

  // Preload de mobile hero-foto via React 19's preload API. Inject een
  // <link rel="preload"> in de <head> tijdens render → browser begint
  // direct te fetchen, vóór CSS/JS parsing. media-attribuut zorgt dat
  // het alleen op mobiel preloadt; op desktop wordt het genegeerd.
  // Resultaat: 'Vertraging bij resource laden' van 3.22s naar ~0.2s.
  if (content.mobile_hero_image_src) {
    preload(content.mobile_hero_image_src, {
      as: "image",
      fetchPriority: "high",
      media: "(max-width: 640px)",
    });
  }

  // city is optioneel — op het hoofddomein is er geen plaatsnaam.
  const city = (content.city ?? "").trim();
  const hasCity = city.length > 0;

  // Auto-scroll verwijderd — was incompatibel met Lighthouse meting (gaf
  // NO_LCP error doordat de scroll plaats vond tijdens LCP-detectie).
  // Header (~70px) + breadcrumb (~40px) + image (320px) = 430px past op
  // alle moderne telefoons (iPhone SE ~568px viewport / iPhone 14 ~844px)
  // ruim boven de fold zonder JS-trickery.

  // Zonder city gebruiken we altijd de city-vrije default — vermijdt lelijke
  // "in , volledig" strings wanneer een template met {{city}} leeg wordt
  // gerenderd.
  const eyebrow = hasCity
    ? interpolate(content.eyebrow ?? "Specialist in toekomstbestendige badkamers {{city}}", city)
    : content.eyebrow && !hasCityPlaceholder(content.eyebrow)
    ? content.eyebrow
    : "Specialist in toekomstbestendige badkamers";

  const headlineLead = content.headline_lead ?? "Badkamerrenovatie";
  // Zonder city verbergen we de cursieve regel volledig.
  const rawHeadlineItalic = content.headline_italic;
  const headlineItalic =
    hasCity
      ? interpolate(rawHeadlineItalic ?? "{{city}}", city)
      : rawHeadlineItalic && !/\{\{\s*city\s*\}\}/i.test(rawHeadlineItalic)
      ? rawHeadlineItalic
      : "";
  const headlineTail = content.headline_tail ?? "— veilig, snel en zorgeloos.";

  const body = hasCity
    ? interpolate(
        content.body ??
          "Een nieuwe toekomstbestendige badkamer in {{city}}, volledig op maat geplaatst in gemiddeld 2-5 dagen. Wij ontzorgen u van begin tot eind.",
        city
      )
    : content.body && !hasCityPlaceholder(content.body)
    ? content.body
    : "Een nieuwe toekomstbestendige badkamer op maat, geplaatst in gemiddeld 2-5 dagen. Wij ontzorgen u van begin tot eind.";

  const ctaText = content.cta_text ?? "Vraag uw brochure aan";
  const trustItems = content.trust_items ?? [
    { icon: "check", label: "Gratis · geen verplichtingen" },
    { icon: "clock", label: "In 30 seconden aangevraagd" },
  ];
  const googleScore = content.google_score ?? 9.2;
  const googleScoreLabel = hasCity
    ? interpolate(content.google_score_label ?? "Score · door klanten in de regio {{city}}", city)
    : content.google_score_label && !hasCityPlaceholder(content.google_score_label)
    ? content.google_score_label
    : "Score · door onze klanten";

  return (
    <section
      className={`hba-section${content.mobile_hero_image_src ? " hba-has-mobile-hero" : ""}${isZwaluw ? " hba-zw" : ""}${content.form_position === "left" ? " hba-section--form-left" : ""}`}
      style={{
        background: T.bg,
        fontFamily: "'Inter', system-ui, sans-serif",
        color: T.ink,
        padding: "32px 0 48px",
        position: "relative" as const,
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes hba-spin { to { transform: rotate(360deg); } }
        /* Default: pijl wijst naar rechts (forward action). Mobile media-query
           hieronder roteert 'm naar beneden zodat hij naar het form eronder wijst.
           Expliciete reset voorkomt browser-cache issues waarbij oude rotatie
           bleef hangen op desktop. */
        .hba-cta-arrow { transform: rotate(0deg); }
        /* Laptop-vriendelijke compactering — hero past zonder scrollen op
            standaard laptop-viewports (1366x768, 1440x900, 1500x680, 1600x900).
            Eerder triggerde dit alleen bij max-height 820 — niet voldoende voor
            1440x900 / 1600x900 laptops waar nog steeds te veel witruimte zat. */
        @media (min-width: 1025px) and (max-height: 950px) {
          .hba-section { padding: 16px 0 28px !important; }
          .hba-outer { padding: 18px 32px !important; }
          .hba-banner-promo { padding: 14px 20px 16px !important; }
          .hba-time-block { padding: 5px 8px !important; min-width: 38px !important; }
          .hba-form-inner { padding: 20px 24px 16px !important; }
          .hba-left-card { padding-top: 4px !important; }
        }
        @media (max-width: 1024px) {
          .hba-grid { grid-template-columns: 1fr !important; gap: 36px !important; padding: 0 !important; background: transparent !important; box-shadow: none !important; border-radius: 0 !important; }
          .hba-outer { padding: 0 !important; }
          .hba-bg-image { display: none !important; }
          /* Tekst eerst, form eronder op tablet+mobiel (HTML-volgorde aanhouden). */
          /* hba-grid--form-left flag heeft GEEN mobile-effect: vanaf 1024px
             stapelen alle hero's gestapeld, form-position is desktop-only. */
        }
        /* ── Opt-in: form-left layout (alleen pages met content.form_position="left") ── */
        /* Order swap geldt op ALLE viewports zodat mobile ook form-first toont. */
        .hba-grid.hba-grid--form-left .hba-left-card { order: 2 !important; }
        .hba-grid.hba-grid--form-left .hba-right-form { order: 1 !important; }
        /* Form-left: alleen Hans + voor-na video tonen in rechter-card op desktop.
           :where() neutraliseert de :not specificity zodat latere class-rules
           (zoals mobile USPs override) deze rule kunnen overschrijven. */
        .hba-grid--form-left .hba-left-card > *:where(:not(.hba-zw-hans):not(.hba-voor-na-video)) { display: none !important; }
        .hba-grid--form-left .hba-left-card .hba-zw-hans { display: grid !important; }
        .hba-grid--form-left .hba-left-card .hba-voor-na-video { display: block !important; }

        @media (min-width: 1025px) {
          .hba-grid.hba-grid--form-left {
            grid-template-columns: 1.05fr 0.95fr !important;
            /* Stretch beide kolommen naar gelijke height zodat we de video
               kunnen uitlijnen met de onderkant van het formulier. Override
               de inline alignItems="start" van de grid. */
            align-items: stretch !important;
          }
          .hba-grid.hba-grid--form-left .hba-left-card {
            /* Flex column zodat margin-top: auto op de video werkt. */
            display: flex !important;
            flex-direction: column !important;
          }
          /* Video onderkant uitlijnen met form-bottom: auto-margin duwt
             de video naar beneden binnen de flex-column. */
          .hba-grid.hba-grid--form-left .hba-voor-na-video {
            margin-top: auto !important;
          }
        }
        /* ── Mobile (<=1024): form first → Hans → video → USPs ── */
        @media (max-width: 1024px) {
          /* Header foto-overlay weg in form-left mode. */
          .hba-section.hba-section--form-left .hba-mobile-hero { display: none !important; }
          /* USPs WEL tonen op mobile (zijn op desktop verborgen door hide-rule). */
          .hba-grid--form-left .hba-left-card > .hba-mobile-usps {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            margin: 4px 0 0;
          }
          /* Wat extra spacing tussen elementen op mobile binnen left-card. */
          .hba-grid--form-left .hba-left-card { gap: 18px; display: flex; flex-direction: column; }
        }
        /* Bekend-van-TV badge weg in form-left mode. */
        .hba-grid--form-left .hba-zw-hans-badge { display: none !important; }
        /* Dedicated trust-line onder het formulier, alleen in form-left mode. */
        .hba-form-trust-below { display: none; }
        .hba-grid--form-left .hba-form-trust-below {
          display: flex; align-items: center; justify-content: center; gap: 18px;
          margin-top: 14px; font-size: 13px; color: var(--color-stone, #5A6578);
          flex-wrap: wrap;
        }
        /* Mobile-only hero foto met overlay — alleen op smal scherm.
           Wordt verborgen op tablet+ zodat de bestaande grid-layout werkt.
           Op desktop wordt de img-tag binnen <picture> niet gefetched dankzij
           <source media>; alleen de fallback-img zou kunnen laden — die
           hebben we ook achter display:none geplaatst. */
        .hba-mobile-hero { display: none; }
        .hba-mobile-hero-img { display: none; }
        /* 3 USP-chips alleen op mobiel zichtbaar — op desktop verborgen. */
        .hba-mobile-usps { display: none; }
        @media (max-width: 640px) {
          .hba-section { padding: 0 0 32px !important; }
          .hba-grid { padding: 12px 20px 0 !important; gap: 24px !important; }
          .hba-mobile-hero {
            display: block !important;
            position: relative;
            width: 100vw;
            margin-left: calc(-50vw + 50%);
            height: 320px;
            overflow: hidden;
            margin-bottom: 8px;
          }
          .hba-mobile-hero-img {
            display: block !important;
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            z-index: 0;
          }
          .hba-mobile-hero-overlay {
            position: absolute;
            inset: 0;
            /* Donker gradient bovenaan voor wit-tekst leesbaarheid, fade naar
               transparant onderaan zodat het bad/product zichtbaar blijft. */
            background: linear-gradient(to bottom, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.12) 40%, rgba(0,0,0,0) 70%);
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            padding: 18px 20px 20px;
            z-index: 1;
            color: #fff;
          }
          .hba-mobile-hero-eyebrow {
            display: inline-flex;
            align-items: center;
            padding: 5px 12px 5px 10px;
            border-radius: 999px;
            background: rgba(255,255,255,0.18);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
            color: #fff;
            font-size: 12px;
            font-weight: 600;
            align-self: flex-start;
            margin-bottom: 12px;
            border: 1px solid rgba(255,255,255,0.25);
          }
          .hba-mobile-hero-h1 {
            font-family: 'Fraunces', 'Playfair Display', Georgia, serif;
            font-size: 30px;
            line-height: 1.05;
            letter-spacing: -0.02em;
            font-weight: 500;
            color: #fff;
            margin: 0;
            text-shadow: 0 2px 16px rgba(0,0,0,0.35);
          }
          /* Verberg eyebrow-pill + h1 in regulier grid wanneer mobile-hero
             actief is (zou anders dubbel verschijnen). */
          .hba-has-mobile-hero .hba-eyebrow-pill,
          .hba-has-mobile-hero .hba-h1 { display: none !important; }
          /* CTA + trust + Google card altijd verbergen op mobile — bottom sticky
             CTA dekt de actie af, USPs in hero vervangen trust-regel, Google-pill
             in mobile-hero-foto vervangt de body Google-card. Geldt voor alle
             landingspaginas. */
          .hba-cta-row,
          .hba-trust-items,
          .hba-google-card-wrap { display: none !important; }
          /* 3-USP rij onder body op mobiel — witte kaarten naast elkaar
             met theme-kleur icoon in zachte cirkel. Vervangt het zware
             UspBlock dat te dominant oogt op smal scherm. */
          .hba-mobile-usps {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            margin: 0 0 28px;
          }
          .hba-mobile-usp {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 8px;
            padding: 14px 6px 12px;
            background: #fff;
            border: 1px solid rgba(20,28,40,0.08);
            border-radius: 14px;
            box-shadow: 0 4px 12px -8px rgba(20,28,40,0.10);
          }
          .hba-mobile-usp-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            border-radius: 999px;
            background: var(--sage-light, #D3DDE8);
          }
          .hba-mobile-usp-label {
            font-size: 12px;
            font-weight: 600;
            color: var(--ink, #1A201D);
            line-height: 1.2;
          }
          /* Google review-kaart in hero-foto, rechts-onder. Witte chip met
             subtiele schaduw, subscores compact (G-logo + sterren + score). */
          .hba-mobile-hero-google {
            position: absolute;
            bottom: 12px;
            right: 12px;
            z-index: 2;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 10px 6px 8px;
            border-radius: 999px;
            background: rgba(255,255,255,0.95);
            backdrop-filter: blur(6px);
            -webkit-backdrop-filter: blur(6px);
            box-shadow: 0 4px 14px -4px rgba(0,0,0,0.25);
          }
          /* Custom Google-review afbeelding (van admin-upload) — zelfde positie
             als de auto-pill. Witte achtergrond + lichte schaduw zodat het
             altijd leesbaar is, ongeacht foto-helderheid. */
          .hba-mobile-hero-google-img {
            position: absolute;
            bottom: 12px;
            right: 12px;
            z-index: 2;
            background: rgba(255,255,255,0.95);
            backdrop-filter: blur(6px);
            -webkit-backdrop-filter: blur(6px);
            border-radius: 12px;
            padding: 6px;
            box-shadow: 0 4px 14px -4px rgba(0,0,0,0.25);
          }
          /* Trust-items naast elkaar houden op mobiel — kleinere gap + font
              zodat 'Gratis · geen verplichtingen' + 'In 30 seconden aangevraagd'
              op één regel passen ipv stacken. */
          .hba-trust-items { gap: 10px !important; font-size: 12px !important; }
          /* Google review-card centreren op mobiel — voelt visueel netter
              dan links-uitgelijnd in een gestackte single-column. */
          .hba-google-card-wrap { text-align: center !important; }
          /* CTA pijl wijst naar beneden op mobiel — naar het form eronder.
              Op desktop blijft 'ie naar rechts (forward action). */
          .hba-cta-arrow { transform: rotate(90deg) !important; }
          /* CTA-knop centreren op mobiel — visueel rustiger in single-col */
          .hba-cta-row { justify-content: center !important; }
          /* Trust-items ook gecentreerd onder de knop */
          .hba-trust-items { justify-content: center !important; }
          /* Zwaluw R01 sfeer-bg uit op tablet/mobiel — opzet blijft gelijk aan
             huidige BW/Veiligdouchen mobile-hero per user-instructie. */
          .hba-zw-bg { display: none !important; }
        }
        /* Zwaluw desktop-tweaks: Google-card weg, CTA + trust-items uitgelijnd
           met onderkant van het formulier. Alleen vanaf md+, mobile blijft 1:1. */
        @media (min-width: 1025px) {
          .hba-zw .hba-google-card-wrap { display: none !important; }
          .hba-zw .hba-grid { align-items: stretch !important; }
          .hba-zw .hba-left-card { display: flex !important; flex-direction: column !important; }
          .hba-zw .hba-cta-row { margin-top: auto !important; }
        }
        /* Zwaluw promo-banner: diep sage donker met koper-accenten (premium,
           brand-consistent, urgency via koper ipv schreeuwerige kleur). */
        .hba-zw .hba-banner-promo {
          background: linear-gradient(135deg, #2C3531 0%, #1F2624 100%) !important;
          border-color: #2C3531 !important;
        }
        /* Strepen-overlay weghalen — schoon. */
        .hba-zw .hba-banner-promo > [aria-hidden] {
          display: none !important;
        }
        /* Cadeau-icoon kader: koper-rand met subtiele copper-tint vulling */
        .hba-zw .hba-banner-promo > div:not([aria-hidden]) > div:first-child {
          background: rgba(184, 146, 90, 0.14) !important;
          border-color: rgba(184, 146, 90, 0.55) !important;
        }
        /* Eyebrow (LENTE-ACTIE) in koper */
        .hba-zw .hba-banner-promo > div:not([aria-hidden]) > div:nth-child(2) > div:first-child {
          color: #C9A86A !important;
          opacity: 1 !important;
        }
        /* Countdown getallen in koper, kader iets warmer */
        .hba-zw .hba-banner-promo .hba-time-block {
          border-color: rgba(184, 146, 90, 0.30) !important;
          background: rgba(0,0,0,0.30) !important;
        }
        .hba-zw .hba-banner-promo .hba-time-block > div:first-child {
          color: #C9A86A !important;
        }
      `}</style>
      {/* Zwaluw R01: sfeer-marmer-crema bg rechts met cream-fade naar links.
          Alleen desktop (verborgen via hba-zw-bg media-query hierboven).
          Next.js Image fill voor automatische WebP + responsive sizes via CDN. */}
      {isZwaluw ? (
        <div
          aria-hidden
          className="hba-zw-bg"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <NextImage
            src="/zwaluw/sfeer-marmer-crema.jpg"
            alt=""
            aria-hidden
            fill
            sizes="(max-width: 1024px) 0px, 100vw"
            quality={75}
            style={{ objectFit: "cover", objectPosition: "center right" }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(100deg, var(--color-bg, #FAF8F2) 0%, var(--color-bg, #FAF8F2) 32%, rgba(250,248,242,0.92) 50%, rgba(250,248,242,0.55) 75%, rgba(250,248,242,0.30) 100%)",
            }}
          />
        </div>
      ) : null}
      {content.image_src && !isZwaluw ? (
        <NextImage
          className="hba-bg-image"
          src={content.image_src}
          alt={
            content.image_alt
              ? (hasCity
                  ? content.image_alt.replace(/\{\{\s*city\s*\}\}/gi, city)
                  : content.image_alt.replace(/\s*\{\{\s*city\s*\}\}\s*/gi, " ").trim())
              : buildImageAlt("hero", {
                  ...(hasCity ? { city } : {}),
                  ...(niche ? { pageSlug: niche } : {}),
                })
          }
          fill
          priority
          sizes="100vw"
          quality={80}
          style={{ objectFit: "cover", zIndex: 0 }}
        />
      ) : null}
      {/* MOBILE-ONLY HERO IMAGE OVERLAY — toont 320px hoge foto bovenaan met
          eyebrow-badge + headline in wit eroverheen. Geeft senior direct
          visuele bevestiging dat hij op de juiste cluster-pagina is. Op desktop
          (>640px) verborgen via CSS — daar blijft de bestaande layout actief. */}
      {content.mobile_hero_image_src ? (
        <div
          className="hba-mobile-hero"
          aria-hidden={false}
        >
          {/* <picture> met media-source: alleen op <640px wordt de foto
              opgehaald (LCP-kandidaat met fetchpriority=high). Op desktop
              matcht de source niet → geen waste. img-tag is verplicht voor
              Lighthouse om LCP te detecteren (bg-image had 2.89s laad-delay). */}
          <picture>
            <source media="(max-width: 640px)" srcSet={content.mobile_hero_image_src} />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={content.mobile_hero_image_src}
              alt={
                content.mobile_hero_image_alt
                  ? (hasCity
                      ? content.mobile_hero_image_alt.replace(/\{\{\s*city\s*\}\}/gi, city)
                      : content.mobile_hero_image_alt.replace(/\s*\{\{\s*city\s*\}\}\s*/gi, " ").trim())
                  : buildImageAlt("hero", {
                      ...(hasCity ? { city } : {}),
                      ...(niche ? { pageSlug: niche } : {}),
                    })
              }
              fetchPriority="high"
              decoding="async"
              loading="eager"
              className="hba-mobile-hero-img"
            />
          </picture>
          <div className="hba-mobile-hero-overlay">
            <div className="hba-mobile-hero-eyebrow">
              <span style={{ width: 6, height: 6, borderRadius: 999, background: "#fff", display: "inline-block", marginRight: 8 }} />
              {eyebrow}
            </div>
            <h1 className="hba-mobile-hero-h1">
              {headlineLead}
              {headlineItalic ? (
                <>
                  <br />
                  <span style={{ fontStyle: "italic", opacity: 0.95 }}>{headlineItalic}</span>
                </>
              ) : null}
              {headlineTail ? <> {headlineTail}</> : null}
            </h1>
          </div>
          {/* Google review als overlay rechtsonderin de hero-foto. Custom
              afbeelding (zoals Google-listing screenshot) krijgt voorrang;
              anders een auto-rendered pill met G-logo + sterren + score. */}
          {content.mobile_google_review_image_src ? (
            <div className="hba-mobile-hero-google-img">
              <NextImage
                src={content.mobile_google_review_image_src}
                alt={content.mobile_google_review_image_alt ?? "Google reviews"}
                width={140}
                height={48}
                quality={85}
                style={{ height: "auto", width: "auto", maxWidth: 160, maxHeight: 64, objectFit: "contain", borderRadius: 10 }}
              />
            </div>
          ) : (
            <div className="hba-mobile-hero-google">
              <svg width="20" height="20" viewBox="0 0 48 48" aria-label="Google">
                <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
                <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
              </svg>
              <span style={{ display: "inline-flex", gap: 1 }}>
                {[0,1,2,3,4].map((i) => (
                  <svg key={i} width="11" height="11" viewBox="0 0 24 24" fill="#E8A33D">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </span>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1A201D", letterSpacing: "-0.01em" }}>
                {googleScore.toString().replace(".", ",")} / 10
              </span>
            </div>
          )}
        </div>
      ) : null}
      {/* Outer wrapper — geeft de marges aan de zijkanten waar de foto
          doorheen kijkt op desktop wanneer image_src is gezet. */}
      <div
        className="hba-outer"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: content.image_src && !isZwaluw ? "32px 40px" : "0",
          position: "relative" as const,
          zIndex: 1,
        }}
      >
      <div
        className={`hba-grid${content.form_position === "left" ? " hba-grid--form-left" : ""}`}
        style={{
          // Zwaluw: geen witte card-frame om het hero-veld (sfeer-marmer-bg
          // zit op section-niveau). Andere brands met image_src behouden de
          // witte card-stijl voor leesbaarheid over de hero-foto.
          ...(content.image_src && !isZwaluw
            ? {
                background: "#FFFFFF",
                borderRadius: 24,
                padding: "40px 18px",
                boxShadow: "0 24px 60px -24px rgba(20,28,40,0.30)",
                overflow: "hidden",
              }
            : { padding: "40px 28px 0" }),
          display: "grid",
          gridTemplateColumns: "0.95fr 1.05fr",
          gap: content.image_src && !isZwaluw ? 24 : 64,
          alignItems: "start",
        }}
      >
        {/* LEFT — content zit op de gedeelde witte wrapper, geen eigen card */}
        <div className="hba-left-card" style={{ paddingTop: 12 }}>
          <div
            className="hba-eyebrow-pill"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px 6px 10px",
              borderRadius: 999,
              background: T.accentSoft,
              color: T.accentDark,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: T.accent }} />
            {eyebrow}
          </div>

          <h1
            className="hba-h1"
            style={{
              fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
              fontSize: "clamp(40px, 4.6vw, 64px)",
              lineHeight: 1.02,
              letterSpacing: "-0.022em",
              margin: "20px 0 0",
              fontWeight: 500,
              color: T.ink,
            }}
          >
            {headlineLead}
            {headlineItalic ? (
              <>
                <br />
                <span style={{ fontStyle: "italic", color: T.accent }}>{headlineItalic}</span>
              </>
            ) : null}
            {headlineTail ? <> {headlineTail}</> : null}
          </h1>

          <p
            style={{
              fontSize: 18,
              lineHeight: 1.55,
              color: T.muted,
              maxWidth: 520,
              margin: "22px 0 32px",
            }}
          >
            {body}
          </p>

          {/* Zwaluw R01: Hans Kazàn-strip onder de lede (alleen desktop) */}
          {isZwaluw ? (
            <div
              className="hba-zw-hans"
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: 20,
                alignItems: "center",
                margin: "0 0 28px",
                maxWidth: 540,
              }}
            >
              <div
                aria-label="Hans Kazàn"
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: 14,
                  backgroundImage: "url('/zwaluw/hans-kazan.jpg')",
                  backgroundColor: "var(--color-bg-200, #E7E1D3)",
                  backgroundPosition: "62% 16%",
                  backgroundSize: "210% auto",
                  backgroundRepeat: "no-repeat",
                  flexShrink: 0,
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                  <span
                    style={{
                      fontSize: "10.5px",
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "var(--color-secondary-700, #6E8A7E)",
                    }}
                  >
                    Ambassadeur
                  </span>
                  <strong
                    style={{
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontStyle: "italic",
                      fontWeight: 500,
                      fontSize: 18,
                      color: "var(--color-ink, #1F2624)",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    Hans Kazàn
                  </strong>
                  <span
                    className="hba-zw-hans-badge"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 10px 4px 8px",
                      background: "var(--color-primary, #D96A2A)",
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      borderRadius: 4,
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="2" y="5" width="20" height="13" rx="2" />
                      <polyline points="8 21 12 17 16 21" />
                    </svg>
                    Bekend van TV
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontStyle: "italic",
                    fontSize: "14.5px",
                    lineHeight: 1.4,
                    color: "var(--color-ink, #1F2624)",
                    letterSpacing: "-0.005em",
                  }}
                >
                  &quot;Een veilige badkamer is geen luxe — het is een vanzelfsprekendheid.&quot;
                </div>
              </div>
            </div>
          ) : null}

          {/* Voor-na video onder Hans — alleen in form-left mode.
              Source komt uit content.voor_na_video_src (per-page override
              in DB). Default: Zwaluw transformation MP4. Loop, muted,
              autoplay — geen interactie vereist, scroll-stop visueel. */}
          {content.form_position === "left" && content.voor_na_video_src ? (
            <div className="hba-voor-na-video" style={{
              marginTop: 20,
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 14px 32px -12px rgba(20,28,40,0.28)",
              border: "1px solid var(--color-line, rgba(20,28,40,0.08))",
              maxWidth: 540,
            }}>
              <video
                src={content.voor_na_video_src}
                {...(content.voor_na_video_poster ? { poster: content.voor_na_video_poster } : {})}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label={content.voor_na_video_alt ?? "Voor en na: badkamer renovatie in 1 dag"}
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  aspectRatio: content.voor_na_video_aspect_ratio ?? "4 / 5",
                  objectFit: "cover",
                  background: "var(--color-bg-200, #E7E1D3)",
                }}
              />
              {content.voor_na_video_caption ? (
                <div style={{
                  padding: "8px 14px",
                  fontSize: 12.5,
                  color: "var(--color-stone, #5A6578)",
                  fontStyle: "italic",
                  textAlign: "center",
                  background: "var(--color-bg-100, #FFFEF9)",
                }}>
                  {content.voor_na_video_caption}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* USP-row onder de body — 3 voordelen naast elkaar. Vervangt
              het zwarte UspBlock (te zwaar visueel). Op desktop verborgen
              via CSS — alleen op <640px zichtbaar. Default = snelheid-claim
              "Binnen 1 dag" — overrijdbaar via content.mobile_usps zodat
              badkamer-renovatie cluster "2-5 dagen" kan tonen. */}
          {(() => {
            const defaultUsps = [
              { icon: "clock", label_top: "Binnen", label_bottom: "1 dag" },
              { icon: "tool", label_top: "Volledig", label_bottom: "op maat" },
              { icon: "medal", label_top: "Eigen", label_bottom: "vakmensen" },
            ];
            const usps = (content.mobile_usps && content.mobile_usps.length > 0) ? content.mobile_usps : defaultUsps;
            return (
              <div className="hba-mobile-usps">
                {usps.slice(0, 3).map((u, i) => (
                  <div className="hba-mobile-usp" key={i}>
                    <span className="hba-mobile-usp-icon"><Icon name={u.icon ?? "clock"} size={20} color={T.accentDark} /></span>
                    <span className="hba-mobile-usp-label">{u.label_top ?? ""}{u.label_bottom ? <><br/>{u.label_bottom}</> : null}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="hba-cta-row" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 18 }}>
            <a
              href="#brochure-form"
              style={{
                padding: "15px 26px",
                borderRadius: 999,
                background: T.cta,
                color: "white",
                fontSize: 16,
                fontWeight: 700,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                boxShadow: "0 14px 28px -8px rgba(0,0,0,0.25)",
              }}
            >
              {ctaText}
              <span className="hba-cta-arrow" style={{ display: "inline-flex", transition: "transform 200ms" }}>
                <Icon name="arrow" size={16} color="white" />
              </span>
            </a>
          </div>

          <div className="hba-trust-items" style={{ display: "flex", alignItems: "center", gap: 18, fontSize: 13, color: T.muted, flexWrap: "nowrap" }}>
            {trustItems.map((t, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                <Icon name={t.icon ?? "check"} size={13} color={T.accent} />
                {t.label}
              </span>
            ))}
          </div>

          <div className="hba-google-card-wrap" style={{ marginTop: 28 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 14,
                padding: "12px 18px",
                borderRadius: 14,
                background: T.card,
                border: `1px solid ${T.border}`,
                boxShadow: "0 6px 18px -10px rgba(31,26,23,0.12)",
              }}
            >
              <svg width="28" height="28" viewBox="0 0 48 48" aria-label="Google">
                <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
                <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
              </svg>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-flex", gap: 1 }}>
                    {[0,1,2,3,4].map((i) => (
                      <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill="#E8A33D">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 18, color: T.ink, letterSpacing: "-0.01em" }}>
                    {googleScore.toString().replace(".", ",")} / 10
                  </span>
                </div>
                <div style={{ fontSize: 12, color: T.muted }}>{googleScoreLabel}</div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: promo + form */}
        <div className="hba-right-form" style={{ position: "relative" }} id="brochure-form">
          {content.promo?.enabled !== false && (content.promo?.title || content.promo?.auto_seasonal || content.promo?.action_data) ? (
            <CountdownBanner promo={content.promo} />
          ) : null}
          <MultiStepForm
            content={content}
            domain={domain}
            niche={niche}
            city={city}
            hasBanner={content.promo?.enabled !== false && !!(content.promo?.title || content.promo?.auto_seasonal || content.promo?.action_data)}
          />
          {/* Trust-line onder form — alleen zichtbaar in form-left mode
              (vervangt de in-card hba-trust-items die door CSS verborgen wordt). */}
          <div className="hba-form-trust-below">
            {trustItems.map((t, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                <Icon name={t.icon ?? "check"} size={13} color={T.accent} />
                {t.label}
              </span>
            ))}
          </div>
        </div>
      </div>
      </div>{/* /.hba-outer */}
    </section>
  );
}

// ── Countdown banner ───────────────────────────────────────────────────────
// ── Seasonal-discount helpers ──────────────────────────────────────────────
// Label rolleert per kwartaal; rollende 12-daagse deadline creëert
// constante urgentie zonder dat admins de actie handmatig moeten updaten.

function currentSeasonDiscount(now: Date = new Date()): string {
  const m = now.getMonth(); // 0=Jan
  if (m >= 2 && m <= 4) return "Voorjaarskorting";
  if (m >= 5 && m <= 7) return "Zomerkorting";
  if (m >= 8 && m <= 10) return "Najaarskorting";
  return "Winterkorting";
}

function rollingDeadline(cycleDays: number = 12, now: number = Date.now()): number {
  // Vaste epoch zodat ieder device dezelfde cyclus ziet
  const epoch = Date.UTC(2026, 0, 1, 0, 0, 0);
  const ms = cycleDays * 24 * 60 * 60 * 1000;
  const cycles = Math.floor((now - epoch) / ms);
  return epoch + (cycles + 1) * ms;
}

function CountdownBanner({ promo }: { promo: NonNullable<HeroBrochureAanvraagContent["promo"]> }) {
  const auto = promo.auto_seasonal === true;
  const deadline = useRef<number>(
    (() => {
      if (auto) return rollingDeadline(12);
      if (promo.deadline_iso) {
        const parsed = Date.parse(promo.deadline_iso);
        if (!Number.isNaN(parsed)) return parsed;
      }
      const d = new Date();
      return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59).getTime();
    })()
  ).current;
  const { d, h, m, s } = useCountdown(deadline);

  const end = new Date(deadline);
  const dayLabel = `${end.getDate()} ${DUTCH_MONTHS[end.getMonth()]}`;
  // Prioriteit: (1) gekoppelde actie uit promo_actions, (2) auto-seasonal,
  //             (3) handmatige admin-title
  const displayTitle = promo.action_data?.label
    ?? (auto ? currentSeasonDiscount() : promo.title);
  const displayValueLabel = promo.action_data?.value_label ?? null;

  return (
    <div
      className="hba-banner-promo"
      style={{
        background: `linear-gradient(135deg, ${T.accent} 0%, ${T.accentDark} 100%)`,
        color: "white",
        borderRadius: "20px 20px 0 0",
        padding: "18px 24px 20px",
        border: `1px solid ${T.accent}`,
        borderBottom: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 18,
        flexWrap: "wrap",
        position: "relative",
        zIndex: 1,
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.06) 0 10px, transparent 10px 20px)",
          pointerEvents: "none",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative" }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: "rgba(255,255,255,0.18)",
            border: "1.5px solid rgba(255,255,255,0.35)",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 12v9H4v-9"/><rect x="2" y="7" width="20" height="5" rx="1"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>
          </svg>
        </div>
        <div style={{ lineHeight: 1.15 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", opacity: 0.95, marginBottom: 2 }}>
            {(promo.eyebrow ?? "ACTIE DEZE MAAND").toUpperCase()}
          </div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 600, letterSpacing: "-0.015em" }}>
            {displayTitle}
            {displayValueLabel ? <>{" "}<span style={{ whiteSpace: "nowrap" }}>{displayValueLabel}</span></> : null}
          </div>
          {/* Subtitle: alleen de deadline. Value zit bij de titel. */}
          <div style={{ fontSize: 11.5, opacity: 0.9, marginTop: 2 }}>
            {promo.subtitle
              ? promo.subtitle.replace(/\{\{\s*deadline\s*\}\}/gi, dayLabel)
              : `bij aanvraag vóór ${dayLabel}`}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
        <TimeBlock n={d} label="dagen" />
        <TimeSep />
        <TimeBlock n={h} label="uur" />
        <TimeSep />
        <TimeBlock n={m} label="min" />
        <TimeSep />
        <TimeBlock n={s} label="sec" />
      </div>
    </div>
  );
}

function TimeBlock({ n, label }: { n: number; label: string }) {
  return (
    <div
      className="hba-time-block"
      style={{
        background: "rgba(0,0,0,0.22)",
        borderRadius: 8,
        padding: "6px 10px",
        minWidth: 44,
        textAlign: "center",
        fontVariantNumeric: "tabular-nums",
        border: "1px solid rgba(255,255,255,0.15)",
      }}
    >
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, lineHeight: 1 }}>
        {String(n).padStart(2, "0")}
      </div>
      <div style={{ fontSize: 9, opacity: 0.8, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}
function TimeSep() {
  return <span style={{ opacity: 0.5, fontSize: 16, fontWeight: 700 }}>:</span>;
}

// ── 3-staps formulier ──────────────────────────────────────────────────────
interface FormData {
  aanhef: "Dhr." | "Mevr.";
  achternaam: string;
  telefoon: string;
  email: string;
  postcode: string;
  huisnummer: string;
  straat: string;
  woonplaats: string;
  oppervlakte: string;
  wanneer: string;
  oplossing: string;
}

export function MultiStepForm({
  content,
  domain,
  niche,
  city,
  hasBanner,
}: {
  content: HeroBrochureAanvraagContent;
  domain: DomainConfig;
  niche: string;
  city: string;
  hasBanner: boolean;
}) {
  const localKey = `brochureflow_hba_lead_id:${domain.brochure_page_id}`;
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [data, setData] = useState<FormData>({
    aanhef: "Dhr.",
    achternaam: "",
    telefoon: "",
    email: "",
    postcode: "",
    huisnummer: "",
    straat: "",
    woonplaats: "",
    oppervlakte: "",
    wanneer: "",
    oplossing: "",
  });
  const [leadId, setLeadId] = useState<string | null>(null);
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrError, setAddrError] = useState<string>("");
  // Vd-BE host: skip postcode-lookup volledig (Nominatim heeft te weinig
  // huisnr-data voor BE → te vaak "Adres niet gevonden"). Toon direct 4
  // velden voor handmatige invoer. NL blijft PDOK-auto-fill.
  const isBeHost = (domain.domain ?? "").replace(/^www\./, "").endsWith("veiligdouchen.be");
  const [manualAddress, setManualAddress] = useState(isBeHost);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // Cloudflare Turnstile token — alleen ingevuld als NEXT_PUBLIC_TURNSTILE_SITE_KEY
  // is gezet, anders blijft 'm null en backend skipt verificatie ook.
  const captchaTokenRef = useRef<string | null>(null);
  // Race-guard voor POST /api/brochure-lead. setLeadId() is async, dus tijdens
  // de eerste POST is leadId nog null en zou een tweede caller (bv. handleNext
  // tijdens lopende auto-save) opnieuw POSTen i.p.v. PATCH'en. Resultaat:
  // dubbele lead-rows. Deze ref blijft `true` zolang een create-POST in flight
  // is en blokkeert nieuwe POST-attempts.
  const creatingLeadRef = useRef<boolean>(false);
  // Sync mirror van leadId zodat handleNext (die na een await leeft in een
  // stale closure) de laatste waarde kan lezen — useState is async, useRef
  // niet. Update samen met setLeadId.
  const leadIdRef = useRef<string | null>(null);
  // Ref op de form-card zodat we bij step-transities (1→2→3) terug naar
  // de top van het formulier scrollen. Op mobiel zakt de viewport anders
  // door naar het volgende blok omdat de form-hoogte verandert.
  const formCardRef = useRef<HTMLDivElement | null>(null);

  // Scroll form in beeld bij elke stap-transitie (skip mount). Op mobiel
  // anders verdwijnt de form deels boven de fold doordat de hoogte van
  // step 2/3 anders is dan step 1.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (step === 1) return; // niet op initial mount
    const el = formCardRef.current;
    if (!el) return;
    // Offset: ~16px lucht onder de browser-chrome op mobiel.
    const top = el.getBoundingClientRect().top + window.scrollY - 16;
    window.scrollTo({ top, behavior: "smooth" });
  }, [step]);

  // GTM/GA4 tracking: brochure_page_view fires once on mount.
  // Mirrors BrochureForm — must stay in sync (see lib/gtm.ts contract).
  useEffect(() => {
    if (typeof window === "undefined") return;
    pushDataLayer({
      event: "brochure_page_view",
      page_id: domain.brochure_page_id,
      page_domain: domain.domain,
      niche: niche || "",
    });
  }, [domain.brochure_page_id, domain.domain, niche]);

  // Mirror leadId state in een ref voor sync-reads in async handlers (handleNext
  // leeft in een stale closure na een await — useState is niet beschikbaar).
  useEffect(() => {
    leadIdRef.current = leadId;
  }, [leadId]);

  // Recovery-flow: wanneer de URL `?af=<brochure_token>` bevat (link uit
  // recovery-mail), restore via token i.p.v. localStorage. Vult form pre +
  // schrijft lead_id terug naar localStorage zodat subsequent saves PATCH'en
  // i.p.v. nieuwe row maken. Strip de af-param uit URL na restore zodat
  // refresh 'm niet opnieuw doet.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("af");
    if (!token) return;
    let cancelled = false;
    fetch(`/api/brochure-lead?token=${encodeURIComponent(token)}`)
      .then(async (r) => (r.ok ? ((await r.json()) as { lead: Record<string, unknown> }) : null))
      .then((j) => {
        if (cancelled || !j?.lead) return;
        const l = j.lead as {
          id?: string;
          step_completed?: number;
          first_name?: string | null;
          salutation?: string | null;
          last_name?: string | null;
          email?: string | null;
          phone?: string | null;
        };
        if (l.id) {
          setLeadId(l.id);
          leadIdRef.current = l.id;
          try { window.localStorage.setItem(localKey, l.id); } catch { /* ignore */ }
        }
        setData((d) => ({
          ...d,
          aanhef: ((l.salutation as "Dhr." | "Mevr.") ?? d.aanhef),
          achternaam: l.last_name ?? d.achternaam,
          email: l.email ?? d.email,
          telefoon: l.phone ?? d.telefoon,
        }));
        // Strip af-param uit URL zodat refresh niet opnieuw triggert
        params.delete("af");
        const newQuery = params.toString();
        const newUrl = window.location.pathname + (newQuery ? `?${newQuery}` : "") + window.location.hash;
        window.history.replaceState({}, "", newUrl);
        // Scroll naar form (#brochure-form anchor staat al in URL via mail-link)
        const formEl = document.getElementById("brochure-form");
        if (formEl) formEl.scrollIntoView({ behavior: "smooth", block: "start" });
      })
      .catch(() => { /* ignore — fallback naar localStorage */ });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore-op-mount: haalt bestaande lead-velden op vanuit DB als er een
  // lead_id in localStorage staat (gebruiker kwam terug na refresh of nieuwe sessie)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(localKey);
    if (!stored) return;
    setLeadId(stored);
    let cancelled = false;
    fetch(`/api/brochure-lead?lead_id=${encodeURIComponent(stored)}`)
      .then(async (r) => {
        if (!r.ok) {
          if (r.status === 404) {
            try { window.localStorage.removeItem(localKey); } catch {}
            setLeadId(null);
          }
          return null;
        }
        return (await r.json()) as { lead: Record<string, unknown> };
      })
      .then((j) => {
        if (cancelled || !j?.lead) return;
        const l = j.lead as {
          step_completed?: number;
          first_name?: string | null;
          salutation?: string | null;
          last_name?: string | null;
          email?: string | null;
          phone?: string | null;
          street?: string | null;
          house_number?: string | null;
          postal_code?: string | null;
          city?: string | null;
          solution_type?: string | null;
          budget_range?: string | null;
          timeframe?: string | null;
          special_requirements?: string | null;
        };
        setData((d) => ({
          ...d,
          aanhef: ((l.salutation as "Dhr." | "Mevr.") ?? d.aanhef),
          achternaam: l.last_name ?? d.achternaam,
          email: l.email ?? d.email,
          telefoon: l.phone ?? d.telefoon,
          postcode: l.postal_code ?? d.postcode,
          huisnummer: l.house_number ?? d.huisnummer,
          straat: l.street ?? d.straat,
          woonplaats: l.city ?? d.woonplaats,
          oppervlakte: l.solution_type ?? d.oppervlakte,
          wanneer: l.timeframe ?? d.wanneer,
          oplossing: l.budget_range ?? d.oplossing,
        }));
        // Skip naar de stap waar gebruiker was: step_completed 2 = nu bezig met 3
        const sc = l.step_completed ?? 1;
        if (sc >= 3) setStep(3);
        else if (sc >= 2) setStep(3);
        // sc=1: blijf op step 1 — user klikte "Volgende" nog niet of was draft
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localKey]);

  // Draft-save: elke change in stap 1-velden schrijft debounced naar de DB,
  // zodat we drop-offs opvangen. Creëert rij bij eerste veld; updatet daarna.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (step !== 1) return;
    const hasAny =
      data.achternaam.trim().length > 0 ||
      data.email.trim().length > 0 ||
      data.telefoon.trim().length > 0;
    if (!hasAny) return;
    const timer = setTimeout(() => {
      const attribution = getAttribution() ?? {};
      const payload = {
        step: 1 as const,
        first_name: "",
        salutation: data.aanhef,
        last_name: data.achternaam.trim(),
        email: data.email.trim().toLowerCase(),
        phone: data.telefoon.replace(/\s/g, ""),
        delivery_method: "digital" as const,
        niche,
        source_url: typeof window !== "undefined" ? window.location.href : "",
        honeypot: "",
        ...(content.promo?.action_slug ? { promo_action: content.promo.action_slug } : {}),
        ...attribution,
      };
      if (!leadId) {
        // Race-guard: skip als er al een create-POST in flight is. De volgende
        // useEffect-run (na typ-wijziging of na response) pakt 'm op met de
        // dan beschikbare leadId — dat wordt dan automatisch een PATCH.
        if (creatingLeadRef.current) return;
        creatingLeadRef.current = true;
        fetch("/api/brochure-lead", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => {
            const id = (j as { lead_id?: string } | null)?.lead_id;
            if (id && id !== "honeypot-blocked") {
              setLeadId(id);
              try { window.localStorage.setItem(localKey, id); } catch {}
            }
          })
          .catch(() => {})
          .finally(() => {
            creatingLeadRef.current = false;
          });
      } else {
        fetch(`/api/brochure-lead?lead_id=${encodeURIComponent(leadId)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }).catch(() => {});
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [
    data.aanhef,
    data.achternaam,
    data.email,
    data.telefoon,
    step,
    leadId,
    niche,
    localKey,
  ]);

  const oppervlakteOptions = unwrapStringList(content.form?.oppervlakte_options, DEFAULT_OPPERVLAKTE);
  const wanneerOptions = unwrapStringList(content.form?.wanneer_options, DEFAULT_WANNEER);
  const oplossingOptions = unwrapStringList(content.form?.oplossing_options, DEFAULT_OPLOSSING);

  const update = <K extends keyof FormData>(k: K, v: FormData[K]) =>
    setData((d) => ({ ...d, [k]: v }));

  // Auto-lookup postcode (debounce 450ms → PDOK via /api/postcode)
  useEffect(() => {
    if (step !== 2) return;
    // Als gebruiker handmatig invoert, respecteer dat — geen lookup / geen overschrijf
    if (manualAddress) return;
    const pc = data.postcode.replace(/\s/g, "").toUpperCase();
    const hn = data.huisnummer.trim();
    // NL = 1234AB, BE = 1234 (4 cijfers). Accepteer beide.
    if (!/^(\d{4}[A-Z]{2}|\d{4})$/.test(pc) || hn.length < 1) {
      setData((d) => ({ ...d, straat: "", woonplaats: "" }));
      setAddrError("");
      return undefined;
    }
    const controller = new AbortController();
    setAddrLoading(true);
    setAddrError("");
    const timer = setTimeout(async () => {
      const hit = await lookupPostcode(pc, hn, controller.signal);
      if (controller.signal.aborted) return;
      if (hit) {
        setData((d) => ({ ...d, straat: hit.straat, woonplaats: hit.woonplaats }));
        setAddrError("");
      } else {
        setData((d) => ({ ...d, straat: "", woonplaats: "" }));
        setAddrError("Adres niet gevonden — controleer de postcode of vul handmatig in.");
      }
      setAddrLoading(false);
    }, 450);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [data.postcode, data.huisnummer, step, manualAddress]);

  const canNext =
    step === 1
      ? data.achternaam.trim().length > 1 &&
        data.telefoon.replace(/\D/g, "").length >= 9 &&
        isValidEmail(data.email)
      : step === 2
      ? data.postcode.replace(/\s/g, "").length >= 4 &&
        data.huisnummer.trim().length >= 1 &&
        data.straat.length > 0 &&
        data.woonplaats.length > 0
      : !!data.oppervlakte && !!data.wanneer && !!data.oplossing;

  // Eerste-ontbrekende veld als gebruiksvriendelijke foutmelding terug —
  // gebruikt om de knop ALTIJD aanklikbaar te maken (geen passive disabled-state
  // meer waarop bezoekers dead-klikken; ze krijgen direct feedback).
  function getFirstValidationError(): string | null {
    if (step === 1) {
      if (data.achternaam.trim().length <= 1) return "Vul je achternaam in om door te gaan.";
      if (data.telefoon.replace(/\D/g, "").length < 9) return "Vul een geldig telefoonnummer in (minimaal 9 cijfers).";
      if (!/.+@.+\..+/.test(data.email)) return "Vul een geldig e-mailadres in.";
      return null;
    }
    if (step === 2) {
      if (data.postcode.replace(/\s/g, "").length < 4) return "Vul je postcode in.";
      if (data.huisnummer.trim().length < 1) return "Vul je huisnummer in.";
      if (data.straat.length === 0 || data.woonplaats.length === 0)
        return "Wacht even — we zoeken je adres op. Of vul straat + woonplaats handmatig in.";
      return null;
    }
    // step 3
    if (!data.oppervlakte) return "Kies een oppervlakte om door te gaan.";
    if (!data.wanneer) return "Kies wanneer je wilt starten.";
    if (!data.oplossing) return "Kies een gewenste oplossing.";
    return null;
  }

  async function handleNext() {
    if (submitting) return;
    const validationError = getFirstValidationError();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      if (step === 1) {
        const event_id = genEventId();
        const attribution = getAttribution() ?? {};
        // Wacht max 3 sec op een lopende auto-save POST. Anders zouden we een
        // tweede POST doen i.p.v. PATCH → dubbele lead-row. Polling met 50ms
        // tick is voldoende fijn (auto-save resolved binnen ~200-500ms).
        const waitStart = Date.now();
        while (creatingLeadRef.current && Date.now() - waitStart < 3000) {
          await new Promise((r) => setTimeout(r, 50));
        }
        const existingLeadId = leadIdRef.current;
        const payload = {
          step: 1 as const,
          first_name: "",
          salutation: data.aanhef,
          last_name: data.achternaam,
          email: data.email.trim().toLowerCase(),
          phone: data.telefoon.replace(/\s/g, ""),
          delivery_method: "digital" as const,
          event_id,
          niche,
          source_url: typeof window !== "undefined" ? window.location.href : "",
          honeypot: "",
          ...(captchaTokenRef.current ? { captcha_token: captchaTokenRef.current } : {}),
          ...(content.promo?.action_slug ? { promo_action: content.promo.action_slug } : {}),
          ...attribution,
        };
        let res: Response;
        let leadIdFromResponse: string;
        if (existingLeadId) {
          // Auto-save heeft al een row aangemaakt → PATCH die met de volledige
          // step-1 data (incl. event_id voor CAPI). Hergebruikt event_id_step_1.
          res = await fetch(
            `/api/brochure-lead?lead_id=${encodeURIComponent(existingLeadId)}`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          leadIdFromResponse = existingLeadId;
        } else {
          // Nog geen row — markeer create-in-flight zodat parallelle auto-save
          // tick niet ook POST'et, en doe de create-POST.
          creatingLeadRef.current = true;
          try {
            res = await fetch("/api/brochure-lead", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const body = (await res.json()) as { lead_id: string };
            leadIdFromResponse = body.lead_id;
          } finally {
            creatingLeadRef.current = false;
          }
        }
        setLeadId(leadIdFromResponse);
        leadIdRef.current = leadIdFromResponse;
        try { window.localStorage.setItem(localKey, leadIdFromResponse); } catch {}
        // GTM/GA4 + Meta tracking — must stay in sync with BrochureForm.tsx
        try {
          const hashed = await sha256Hex(data.email);
          if (typeof window !== "undefined" && window.fbq) {
            window.fbq("track", "Lead", {}, { eventID: event_id });
          }
          const step1Event: import("../lib/gtm").BrochureStepEvent = {
            event: "brochure_step_1_complete",
            page_id: domain.brochure_page_id,
            lead_id: leadIdFromResponse,
            event_id,
            delivery_method: "digital",
            lead_hashed_email: hashed,
          };
          if (niche) step1Event.niche = niche;
          // Stap 1 IS de Google Ads conversion — value mee (mirror BrochureForm).
          const cv1 = domain.conversion_values;
          if (cv1 && typeof cv1.brochure_aanvraag_value === "number") {
            step1Event.value = cv1.brochure_aanvraag_value;
            step1Event.currency = cv1.currency;
          }
          pushDataLayer(step1Event);
        } catch { /* tracking errors must not block UX */ }
        setStep(2);
      } else if (step === 2 && leadId) {
        const event_id = genEventId();
        const res = await fetch(`/api/brochure-lead?lead_id=${encodeURIComponent(leadId)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            step: 2,
            street: data.straat.trim(),
            house_number: data.huisnummer.trim(),
            postal_code: data.postcode.replace(/\s/g, "").toUpperCase(),
            city: data.woonplaats.trim(),
            event_id,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        try {
          if (typeof window !== "undefined" && window.fbq) {
            window.fbq("trackCustom", "BrochureStep2", {}, { eventID: event_id });
          }
          const step2Event: import("../lib/gtm").BrochureStepEvent = {
            event: "brochure_step_2_complete",
            page_id: domain.brochure_page_id,
            lead_id: leadId,
            event_id,
            postal_code_prefix: data.postcode.replace(/\s/g, "").slice(0, 4),
          };
          if (niche) step2Event.niche = niche;
          pushDataLayer(step2Event);
        } catch { /* tracking errors must not block UX */ }
        setStep(3);
      } else if (step === 3 && leadId) {
        const event_id = genEventId();
        const special = [
          `Aanhef: ${data.aanhef}`,
          `Oppervlakte: ${data.oppervlakte}`,
        ].join(" | ");
        const res = await fetch(`/api/brochure-lead?lead_id=${encodeURIComponent(leadId)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            step: 3,
            solution_type: data.oplossing,
            timeframe: data.wanneer,
            special_requirements: special,
            event_id,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        try { window.localStorage.removeItem(localKey); } catch {}
        // Onderdruk ExitIntentPopup na succesvolle submit
        try { sessionStorage.setItem("bw_exit_intent_seen_v1", "1"); } catch { /* ignore */ }
        try {
          if (typeof window !== "undefined" && window.fbq) {
            window.fbq("trackCustom", "BrochureStep3", {}, { eventID: event_id });
          }
          const step3Event: import("../lib/gtm").BrochureStepEvent = {
            event: "brochure_step_3_complete",
            page_id: domain.brochure_page_id,
            lead_id: leadId,
            event_id,
            solution_type: data.oplossing,
            timeframe: data.wanneer,
          };
          if (niche) step3Event.niche = niche;
          // Stap 3 = enrichment, GEEN value voor Google Ads — al gefired bij stap 1.
          pushDataLayer(step3Event);
        } catch { /* tracking errors must not block UX */ }
        setDone(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) return <SuccessCard city={data.woonplaats || city} name={data.achternaam} salutation={data.aanhef} />;

  const ctaLabel =
    submitting ? "Bezig…" : step === 1 ? "Nu aanvragen" : step === 2 ? "Doorgaan" : "Afronden";

  const cardStyle: React.CSSProperties = {
    background: T.card,
    borderRadius: hasBanner ? "0 0 20px 20px" : 20,
    border: `1px solid ${T.border}`,
    borderTop: hasBanner ? "none" : `1px solid ${T.border}`,
    boxShadow: "0 30px 60px -24px rgba(31,26,23,0.22), 0 10px 18px -10px rgba(31,26,23,0.08)",
    overflow: "hidden",
    position: "relative",
    zIndex: 2,
  };

  return (
    <div ref={formCardRef} style={cardStyle}>
      <div className="hba-form-inner" style={{ padding: "26px 28px 22px" }}>
        {/* Progress-bars: drie segmenten die oplichten in CTA-kleur naarmate stappen gevuld raken */}
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 4,
                background: i <= step ? T.cta : "#E5E7EB",
                transition: "background 0.3s",
              }}
            />
          ))}
        </div>

        <h2
          lang="nl"
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: "clamp(25px, 6.8vw, 30px)",
            fontWeight: 500,
            letterSpacing: "-0.015em",
            lineHeight: 1.15,
            margin: "2px 0 8px",
            color: T.ink,
            textWrap: "balance",
            hyphens: "auto",
            wordBreak: "normal",
            overflowWrap: "break-word",
          }}
        >
          {step === 1 && (content.form?.step1_title ?? "JA, stuur mij de gratis brochure.")}
          {step === 2 && (content.form?.step2_title ?? "Waar mogen we hem naartoe sturen?")}
          {step === 3 && (content.form?.step3_title ?? "Wij hebben nog wat gegevens nodig")}
        </h2>

        <p style={{ fontSize: 14, color: T.muted, margin: "0 0 22px" }}>
          {step === 1 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Icon name="check" size={14} color={T.cta} />
              In 30 seconden aangevraagd
            </span>
          )}
          {step === 2 && "Zo checken we ook direct uw wachttijd."}
          {step === 3 && "Deze laatste vragen helpen ons om u écht passend advies te geven."}
        </p>

        {error ? (
          <div
            style={{
              background: "#FEF2F2",
              color: "#991B1B",
              border: "1px solid #FECACA",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        ) : null}

        {step === 1 && (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>Aanhef</span>
              {(["Dhr.", "Mevr."] as const).map((opt) => {
                const active = data.aanhef === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => update("aanhef", opt)}
                    style={{
                      padding: "4px 6px",
                      border: "none",
                      background: "transparent",
                      color: T.ink,
                      fontSize: 14,
                      fontWeight: active ? 600 : 500,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 14, height: 14, borderRadius: 999,
                        border: `2px solid ${active ? T.accent : T.border}`,
                        background: active ? T.accent : "transparent",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        boxSizing: "border-box",
                      }}
                    >
                      {active ? <span style={{ width: 5, height: 5, borderRadius: 999, background: "white" }} /> : null}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
            <FieldLabel label="Achternaam" icon="user">
              <input
                type="text"
                value={data.achternaam}
                onChange={(e) => update("achternaam", e.target.value)}
                placeholder="Bijv. de Vries"
                style={inputStyle()}
              />
            </FieldLabel>
            <FieldLabel label="Telefoonnummer" icon="phone">
              <input
                type="tel"
                value={data.telefoon}
                onChange={(e) => update("telefoon", e.target.value)}
                placeholder="06 1234 5678"
                style={inputStyle()}
              />
            </FieldLabel>
            <FieldLabel label="E-mailadres" icon="mail">
              <input
                type="email"
                value={data.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="u@voorbeeld.nl"
                style={inputStyle()}
              />
            </FieldLabel>
            {/* Cloudflare Turnstile — alleen actief als NEXT_PUBLIC_TURNSTILE_SITE_KEY
                is gezet. interaction-only mode toont meestal niets, alleen bij verdacht gedrag. */}
            <TurnstileWidget tokenRef={captchaTokenRef} />
          </div>
        )}

        {step === 2 && (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
              <FieldLabel label="Postcode" icon="pin">
                <input
                  type="text"
                  value={data.postcode}
                  onChange={(e) => update("postcode", e.target.value.toUpperCase())}
                  placeholder={isBeHost ? "2000" : "1012 AB"}
                  maxLength={isBeHost ? 4 : 7}
                  style={inputStyle()}
                  autoFocus
                />
              </FieldLabel>
              <FieldLabel label="Huisnr.">
                <input
                  type="text"
                  value={data.huisnummer}
                  onChange={(e) => update("huisnummer", e.target.value)}
                  placeholder="12"
                  style={inputStyle()}
                />
              </FieldLabel>
            </div>
            {manualAddress ? (
              <div style={{ display: "grid", gap: 12 }}>
                <FieldLabel label="Straatnaam" icon="pin">
                  <input
                    type="text"
                    value={data.straat}
                    onChange={(e) => update("straat", e.target.value)}
                    placeholder="Bijv. Hoofdstraat"
                    style={inputStyle()}
                  />
                </FieldLabel>
                <FieldLabel label="Plaats">
                  <input
                    type="text"
                    value={data.woonplaats}
                    onChange={(e) => update("woonplaats", e.target.value)}
                    placeholder={isBeHost ? "Bijv. Antwerpen" : "Bijv. Amsterdam"}
                    style={inputStyle()}
                  />
                </FieldLabel>
                {/* "Terug naar automatisch invullen" alleen voor NL — BE
                    heeft geen postcode-auto-fill (skip op host-niveau). */}
                {!isBeHost ? (
                  <button
                    type="button"
                    onClick={() => {
                      setManualAddress(false);
                      setData((d) => ({ ...d, straat: "", woonplaats: "" }));
                    }}
                    style={{
                      fontSize: 12,
                      color: T.muted,
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      textDecoration: "underline",
                      textAlign: "left",
                    }}
                  >
                    ← Terug naar automatisch invullen
                  </button>
                ) : null}
              </div>
            ) : (
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: T.accentSoft,
                  border: `1px dashed ${T.border}`,
                  fontSize: 13,
                  color: T.ink,
                  minHeight: 46,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                  {addrLoading ? (
                    <>
                      <span
                        style={{
                          width: 12, height: 12, borderRadius: 999,
                          border: `2px solid ${T.accent}`, borderTopColor: "transparent",
                          animation: "hba-spin 0.8s linear infinite",
                        }}
                      />
                      <span style={{ color: T.muted }}>Adres ophalen…</span>
                    </>
                  ) : data.straat && data.woonplaats ? (
                    <>
                      <Icon name="check" size={16} color={T.accent} />
                      <span><b>{data.straat} {data.huisnummer}</b>, {data.woonplaats}</span>
                    </>
                  ) : data.postcode.length >= 4 && data.huisnummer ? (
                    <span style={{ color: T.muted }}>{addrError || "Controleer postcode en huisnummer."}</span>
                  ) : (
                    <span style={{ color: T.muted }}>Vul postcode en huisnummer in — adres volgt automatisch.</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setManualAddress(true)}
                  style={{
                    fontSize: 11,
                    color: T.accent,
                    background: "none",
                    border: "none",
                    padding: "2px 4px",
                    cursor: "pointer",
                    textDecoration: "underline",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Handmatig
                </button>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div style={{ display: "grid", gap: 18 }}>
            <ChipGroup
              label="Naar welke oplossing zoekt u?"
              options={oplossingOptions}
              value={data.oplossing}
              onChange={(v) => update("oplossing", v)}
            />
            <ChipGroup
              label="Hoe groot is uw badkamer?"
              options={oppervlakteOptions}
              value={data.oppervlakte}
              onChange={(v) => update("oppervlakte", v)}
            />
            <ChipGroup
              label="Wanneer wilt u renoveren?"
              options={wanneerOptions}
              value={data.wanneer}
              onChange={(v) => update("wanneer", v)}
            />
          </div>
        )}

        <div style={{ marginTop: 22, display: "flex", gap: 10 }}>
          {step > 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => (s > 1 ? ((s - 1) as 1 | 2) : s))}
              style={{
                padding: "14px 18px",
                borderRadius: 12,
                border: `1px solid ${T.border}`,
                background: T.card,
                color: T.ink,
                fontSize: 15,
                fontWeight: 500,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontFamily: "inherit",
              }}
            >
              <Icon name="back" size={16} /> Terug
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleNext}
            // Alleen disabled tijdens daadwerkelijke submit — niet meer
            // bij ontbrekende fields. Knop blijft klikbaar zodat de bezoeker
            // direct een uitleg krijgt welk veld nog mist (geen dead-clicks).
            disabled={submitting}
            style={{
              flex: 1,
              padding: "16px 22px",
              borderRadius: 12,
              border: "none",
              background: T.cta,
              color: "white",
              fontSize: 16,
              fontWeight: 700,
              // Altijd cursor:pointer + volle opacity — zelfs bij incomplete
              // fields. Bezoeker krijgt een directe foutmelding via setError
              // bij klik. Dat voorkomt "dood lijkende" knop die dead-clicks
              // veroorzaakt (Clarity-data 18 mei: 22 dead-clicks op 14 sessies).
              cursor: submitting ? "wait" : "pointer",
              opacity: submitting ? 0.6 : 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              fontFamily: "inherit",
              boxShadow: "0 12px 26px -8px rgba(0,0,0,0.25)",
              letterSpacing: "0.01em",
            }}
          >
            {ctaLabel}
            <Icon name="arrow" size={16} color="white" />
          </button>
        </div>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "6px 22px",
            fontSize: 12.5,
            color: "#2A4A35",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, whiteSpace: "nowrap" }}>
            <Icon name="lock" size={14} color="#2F8A48" />
            Versleutelde SSL-verbinding
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, whiteSpace: "nowrap" }}>
            <Icon name="shield" size={14} color="#2F8A48" />
            AVG-proof · 100% privacy
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Kleinere building blocks ──────────────────────────────────────────────
function inputStyle(): React.CSSProperties {
  return {
    width: "100%",
    padding: "14px 14px",
    borderRadius: 10,
    border: `1.5px solid ${T.border}`,
    background: "#FDFBF8",
    fontSize: 15,
    fontFamily: "inherit",
    color: T.ink,
    outline: "none",
    boxSizing: "border-box",
    transition: "border 0.15s, background 0.15s",
  };
}

function FieldLabel({ label, icon, children }: { label: string; icon?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.ink, display: "inline-flex", alignItems: "center", gap: 6 }}>
          {icon ? <Icon name={icon} size={14} color={T.muted} /> : null}
          {label}
        </span>
      </span>
      {children}
    </label>
  );
}

function ChipGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 10 }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              style={{
                padding: "10px 14px",
                borderRadius: 999,
                border: `1.5px solid ${active ? T.accent : T.border}`,
                background: active ? T.accentSoft : T.card,
                color: active ? T.accentDark : T.ink,
                fontSize: 14,
                fontWeight: active ? 600 : 500,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s",
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SuccessCard({ city, name, salutation }: { city: string; name: string; salutation?: string }) {
  // "Bedankt, Dhr. Casander!" — als aanhef + achternaam aanwezig: combineer.
  // Geen aanhef → val terug op alleen achternaam.
  const greeting = name
    ? salutation
      ? `${salutation} ${name}`
      : name
    : "";
  return (
    <div
      style={{
        background: T.card,
        borderRadius: 20,
        border: `1px solid ${T.border}`,
        boxShadow: "0 30px 60px -24px rgba(31,26,23,0.22)",
        padding: "36px 28px 28px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 999,
          background: T.accentSoft,
          color: T.accentDark,
          display: "grid",
          placeItems: "center",
          margin: "0 auto 18px",
        }}
      >
        <Icon name="check" size={30} color={T.accentDark} />
      </div>
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 500, letterSpacing: "-0.015em", margin: "0 0 10px" }}>
        Bedankt{greeting ? `, ${greeting}` : ""}!
      </h2>
      <p style={{ color: T.muted, fontSize: 15, lineHeight: 1.55, margin: 0, maxWidth: 380, marginInline: "auto" }}>
        Uw brochure is verzonden. Check binnen enkele minuten uw mailbox.
      </p>
      <p style={{ color: T.muted, fontSize: 13, lineHeight: 1.5, margin: "10px auto 0", maxWidth: 380, fontStyle: "italic", opacity: 0.85 }}>
        Geen mail ontvangen? Controleer uw spam-/ongewenste mailbox.
      </p>
    </div>
  );
}

function genEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * SchemaForm wraps scalar list items in `{ value: "..." }`. This unwraps them
 * back to plain strings for the ChipGroup. Accepts either shape so we don't
 * break when the author saved via the raw JSON editor.
 */
function unwrapStringList(
  raw: unknown,
  fallback: string[]
): string[] {
  if (!Array.isArray(raw) || raw.length === 0) return fallback;
  const out = raw
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && "value" in item) {
        const v = (item as { value?: unknown }).value;
        return typeof v === "string" ? v : "";
      }
      return "";
    })
    .filter((s) => s.trim().length > 0);
  return out.length > 0 ? out : fallback;
}
